import { navigate } from 'gatsby'
import {
  put,
  select,
  takeEvery,
  call,
  spawn,
  all,
  take,
  delay,
} from 'redux-saga/effects'

import { userLogIn } from '@/store/user'
import {
  setCreatedChargeInfo,
  setCreatedChargeError,
  directBuyNotLoggedIn,
  createCharge,
  acceptCharge,
  declineCharge,
  archiveCharge,
  getCartInfo,
  purchaseCart,
  rejectCartCheckout,
  startPayment,
  startCartCheckout,
} from '@/store/payment'
import {
  createDirectCharge,
  acceptDirectCharge,
  declineDirectCharge,
  archiveDirectCharge,
} from '@/store/directCharges'
import {
  createRecurrentCharge,
  acceptRecurrentCharge,
  declineRecurrentCharge,
  archiveRecurrentCharge,
} from '@/store/recurrentCharges'
import { removeProduct } from '@/store/cart'
import { notificationsCreate } from '@/store/notifications'
import {
  getPaymentCharge,
  getPaymentItem,
  isExternalCharge,
  isProcessableCharge,
  getPaymentCart,
  getCartId,
  getChargeType,
  getItemById,
  hasProductInCart,
  getPaymentIntent,
  getChargeProductType,
} from '@/store/selectors'
import { installApp } from '@/store/installedApps'
import cartApi from '@/apis/cart'
import {
  PAYMENT,
  SENTRY,
  CART,
  RECURRENT_CHARGES,
  DIRECT_CHARGES,
  EVENT_SOURCE_IDS,
  NOTIFICATIONS,
  ToastKind,
} from '@/constants'
import { OK, BAD_REQUEST } from '@/constants/utils'
import { PM_PAYMENT_EVENT } from '@/utils/postMessage/constants'
import { getErrorCode } from '@/utils/api'
import { pushEvent } from '@/utils/analytics'
import { sendPostMessage } from '@/utils/postMessage/utils'
import { PM_EVENT } from '@/utils/postMessage/constants'
import { SagaError, errorHandler } from '@/utils/sagas'
import { isEveryErrorInactiveProduct } from '@/utils/products'
import { load as cartLoad, create as cartCreate } from '@/store/cart'
import { isAgentApp } from '@/utils'

function* acceptChargeSaga() {
  try {
    const item = yield select(getPaymentItem)
    const intent = yield select(getPaymentIntent)
    const cart = yield select(getPaymentCart)
    const charge = yield select(getPaymentCharge)
    const isExternal = yield select(isExternalCharge)

    // Handle purchasing cart flow
    if (cart) {
      yield call(purchaseCartSaga)
      return null
    }

    // Handle purchasing external payments
    if (intent && charge) {
      const productType = yield select(getChargeProductType, item.id)

      switch (intent.metadata.type) {
        case PAYMENT.DIRECT_CHARGE_TYPE:
          yield put(
            acceptDirectCharge.pending({
              paymentId: charge.id,
              appId: item.id,
              slug: item.slug,
              productType,
              isExternalCharge: isExternal,
            })
          )
          break

        case PAYMENT.RECURRENT_CHARGE_TYPE:
          yield put(
            acceptRecurrentCharge.pending({
              paymentId: charge.id,
              appId: item.id,
              slug: item.slug,
              productType,
              isExternalCharge: isExternal,
            })
          )
          break

        default:
      }
    }
  } catch (error) {
    yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
    yield put(setCreatedChargeError({ error: PAYMENT.ERRORS.BILLING_API }))
    yield put(startPayment.rejected({ error: PAYMENT.ERRORS.BILLING_API }))
    throw new SagaError('Accepting charge failed', error)
  }
}

function* declineChargeSaga({ payload }) {
  try {
    const { preventRedirect, continueLoading } = payload || {}
    const item = yield select(getPaymentItem)
    const charge = yield select(getPaymentCharge)
    const externalCharge = yield select(isExternalCharge)
    const isProcessable = yield select(isProcessableCharge)
    let frequency

    if (externalCharge) {
      frequency = yield select(getChargeType)
    } else {
      frequency = item?.payment?.frequency
    }

    if (item && charge && isProcessable) {
      const productType = yield select(getChargeProductType, item.id)

      switch (frequency) {
        case PAYMENT.DIRECT_CHARGE:
          if (charge.status === DIRECT_CHARGES.STATUS.PENDING) {
            yield put(
              declineDirectCharge.pending({
                paymentId: charge.id,
                productType,
              })
            )
            yield take(declineDirectCharge.fulfilled)
          }
          break

        case PAYMENT.RECURRENT_CHARGE:
          if (charge.status === RECURRENT_CHARGES.STATUS.PENDING) {
            yield put(
              declineRecurrentCharge.pending({
                paymentId: charge.id,
                productType,
                continueLoading,
              })
            )
            yield take(declineRecurrentCharge.fulfilled)
          }
          break

        default:
      }
    }

    if (!preventRedirect && externalCharge && charge.return_url) {
      window.location = charge.return_url
    }
  } catch (error) {
    yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
  }
}

function* createChargeSaga({ payload: { paymentIntent } }) {
  try {
    const charge = yield select(getPaymentCharge)
    const isProcessable = yield select(isProcessableCharge)

    if (charge && isProcessable && !isAgentApp) {
      yield put(declineCharge({ preventRedirect: true, continueLoading: true }))
    }

    if (paymentIntent) {
      switch (paymentIntent.metadata.type) {
        case PAYMENT.DIRECT_CHARGE_TYPE: {
          yield put(createDirectCharge.pending({ paymentIntent }))
          break
        }

        case PAYMENT.RECURRENT_CHARGE_TYPE: {
          yield put(createRecurrentCharge.pending({ paymentIntent }))
          break
        }

        default:
      }
    }
  } catch (error) {
    yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
  }
}

function* archiveChargeSaga({ payload }) {
  try {
    switch (payload?.createdChargeType) {
      case PAYMENT.DIRECT_CHARGE:
        yield put(archiveDirectCharge({ json: payload.charge }))
        break

      case PAYMENT.RECURRENT_CHARGE:
        yield put(archiveRecurrentCharge({ json: payload.charge }))
        break

      default:
    }
  } catch (error) {
    yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
  }
}

function* getCartInfoRequestSaga({ payload: { cartId } }) {
  try {
    const { json, status } = yield call(cartApi.get, cartId)
    const { summary, closedAt, checkedOutUntil, purchasedAt } = json

    if (status === OK) {
      const cart = {
        ...summary,
        closedAt,
        checkedOutUntil,
        purchasedAt,
        id: cartId,
      }

      yield put(getCartInfo.fulfilled({ cart }))

      // Refresh the cart only if matching the given cartId
      // On thank you page we are creating new cart, and we don't want to update it
      // on page refresh
      const paymentCartId = yield select(getCartId)

      if (paymentCartId === cartId) {
        yield put(cartLoad.fulfilled(json))
      }
    }
  } catch (error) {
    yield put(getCartInfo.rejected({ error: error?.message }))
    yield spawn(errorHandler, error, SENTRY.TAGS.CART)
  }
}

function* checkPurchasedItems(cartId) {
  const cart = yield call(pollPurchasedCart, cartId)

  const items = cart?.products || []

  return items.reduce(
    (acc, item) => {
      if (item.purchasedAt) {
        acc.purchased.push(item.id)
      } else {
        acc.notPurchased.push(item.id)
      }
      return acc
    },
    { purchased: [], notPurchased: [] }
  )
}

function* pollPurchasedCart(cartId) {
  let retryCount = 0
  let purchasedAt

  while (!purchasedAt) {
    try {
      const { json, status } = yield call(cartApi.get, cartId)

      if (status === OK && retryCount < 10) {
        if (json.purchasedAt) {
          purchasedAt = json.purchasedAt
          return json
        }

        retryCount++
        yield delay(250)
      } else {
        throw new Error(
          `Couldn't check if all products made it through purchase process`
        )
      }
    } catch (error) {
      throw new Error(
        `Couldn't check if all products made it through purchase process`
      )
    }
  }
}

function* installPurchasedApps(itemIdList) {
  try {
    sendPostMessage(PM_EVENT.CART_PURCHASED, {
      purchased_apps_id_list: itemIdList,
    })

    const installedItemsCalls = itemIdList?.map((itemId) =>
      put(installApp.fulfilled({ id: itemId, sourceId: EVENT_SOURCE_IDS.CART }))
    )

    yield all(installedItemsCalls)
  } catch (error) {
    yield spawn(errorHandler, error, SENTRY.TAGS.CHECKOUT)
  }
}

function* purchaseCartSaga() {
  try {
    const cart = yield select(getPaymentCart)

    if (cart) {
      const cartId = cart.id

      switch (true) {
        case cart.purchasedAt:
          throw new SagaError(
            'Cannot purchase already purchased cart',
            null,
            null
          )

        case cart.closedAt:
          throw new SagaError('Cannot purchase closed cart', null, null)

        default: {
          const { status, json } = yield call(cartApi.purchase, cartId)

          if (status === OK) {
            const { purchased, notPurchased } = yield call(
              checkPurchasedItems,
              cartId
            )

            yield put(cartCreate.pending({ products: notPurchased }))
            yield put(getCartInfo.pending({ cartId }))

            yield delay(250)
            yield call(installPurchasedApps, purchased)

            pushEvent({
              event: 'checkoutMarketplace',
              payload: {
                cartId,
                appId: purchased,
                failedAppId: notPurchased,
              },
            })

            const notes = notPurchased.reduce(
              (acc, item) => [...acc, item.note],
              []
            )
            const isOnlyInactiveProductError =
              isEveryErrorInactiveProduct(notes)

            if (!notPurchased.length || isOnlyInactiveProductError) {
              yield call(navigate, `/checkout/cart/${cartId}/thank-you`)
            } else {
              yield call(navigate, `/checkout/cart/${cartId}/review-order`)
            }
            yield put(purchaseCart.fulfilled())
          } else if (status === BAD_REQUEST) {
            const { code, message } = getErrorCode(json && json.errors)

            if (code === CART.ERROR_CODE.PRODUCTS_CART_CHECKSUM) {
              yield put(purchaseCart.rejected({ error: code }))
            } else {
              throw new SagaError(message, json, status)
            }
          } else {
            throw new SagaError(`Purchase couldn't be confirmed`, json, status)
          }
        }
      }
    }
  } catch (error) {
    yield put(purchaseCart.rejected({ error: PAYMENT.ERRORS.CART }))
    yield spawn(errorHandler, error, SENTRY.TAGS.CART)
  }
}

function* startCartCheckoutRequestSaga({ payload: { cartId } }) {
  try {
    const { status } = yield call(cartApi.startCheckout, cartId)

    if (status === OK) {
      yield put(startCartCheckout.fulfilled())
    } else {
      throw new SagaError(
        `Start cart checkout failed [${cartId}]`,
        null,
        status
      )
    }
  } catch (error) {
    yield put(startCartCheckout.rejected({ error: error?.message }))
    yield spawn(errorHandler, error, SENTRY.TAGS.CART)
  }
}

function* rejectCartCheckoutRequestSaga({ payload }) {
  try {
    const shouldRedirect = payload?.redirect
    const cart = yield select(getPaymentCart)

    if (cart && !cart?.purchasedAt && !cart?.closedAt) {
      const { status } = yield call(cartApi.rejectCheckout, cart.id)

      if (status === OK) {
        yield put(rejectCartCheckout.fulfilled())

        if (shouldRedirect) {
          navigate('/cart/')
        }
      } else {
        throw new SagaError(
          `Reject cart checkout failed [${cart.id}]`,
          null,
          status
        )
      }
    }

    return null
  } catch (error) {
    yield put(rejectCartCheckout.rejected({ error: error?.message }))
    yield spawn(errorHandler, error, SENTRY.TAGS.CART)
  }
}

export function* directBuyProductNotLoggedInSaga({ payload }) {
  yield take(userLogIn.fulfilled)

  const item = yield select(getItemById, payload.id)

  if (item) {
    yield call(navigate, `/checkout/${item.slug}?embedded=true`)
  }
}

function* handlePaymentFailed({ payload: { error } }) {
  if (isAgentApp) {
    yield call(sendPostMessage, PM_PAYMENT_EVENT.REGISTER_TRANSACTION_FAILURE, {
      error,
    })
  } else {
    // Scroll to the top every time that Checkout will get an error
    // so users on mobile will get the proper notification about error
    yield call(window.scrollTo, 0, {})
  }
}

function* handlePaymentFulfilled({ payload: { appId } }) {
  const item = yield select(getItemById, appId)
  const isNotCheckoutRelated = !window.location.pathname.includes('/checkout/')
  const shouldDisplayNotification = isNotCheckoutRelated && !isAgentApp

  // Remove app from the cart if it's still there after purchase
  const isProductInCart = yield select(hasProductInCart, item.id)

  if (isProductInCart) {
    yield put(removeProduct.pending({ productId: item.id }))
  }

  if (shouldDisplayNotification) {
    yield put(
      notificationsCreate({
        kind: ToastKind.Success,
        content: "You've successfully purchased the app",
        autoHideDelayTime: NOTIFICATIONS.AUTO_HIDE_DELAY_TIME,
      })
    )
  }
}

function* handleChargeCreation({ payload: { charge, chargeType } }) {
  if (!charge) {
    return null
  }

  yield call(sendPostMessage, PM_PAYMENT_EVENT.REGISTER_TRANSACTION_SUCCESS, {
    charge: { ...charge, type: chargeType },
  })
}

export function* watchPaymentRequests() {
  yield takeEvery(createCharge, createChargeSaga)
  yield takeEvery(acceptCharge, acceptChargeSaga)
  yield takeEvery(declineCharge, declineChargeSaga)
  yield takeEvery(archiveCharge, archiveChargeSaga)
  yield takeEvery(getCartInfo.pending, getCartInfoRequestSaga)
  yield takeEvery(purchaseCart.pending, purchaseCartSaga)
  yield takeEvery(startCartCheckout.pending, startCartCheckoutRequestSaga)
  yield takeEvery(rejectCartCheckout.pending, rejectCartCheckoutRequestSaga)
  yield takeEvery(directBuyNotLoggedIn, directBuyProductNotLoggedInSaga)
  yield takeEvery(startPayment.rejected, handlePaymentFailed)
  yield takeEvery(startPayment.fulfilled, handlePaymentFulfilled)
  yield takeEvery(setCreatedChargeInfo, handleChargeCreation)
}
